// TutorSidebar · panel lateral chat AI Tutor grounded en RAG Robotea.
// Owner: Carlos Beltrán (frontend) · co-owned con Andrés Mora (backend).
// Sprint feat/campus-ia-tutor · 2026-05-27.
//
// UX:
// - Botón flotante "💬 Tutor IA" sticky right desktop (lg+).
// - Panel deslizable derecha (380px) con conversación scrollable + textarea + Enviar.
// - Mobile (<lg): full-screen modal disparado por floating button derecho.
// - Sesión por (user, module_slug) · auto-load última o crea nueva al primer mensaje.
// - 3 suggestions chips al inicio si conversación vacía.
// - Confidence badge bajo respuestas assistant (low/medium/high).
// - CTA Discord #rbm-soporte cuando escalated=true OR confidence<0.4.
//
// Backend: POST/GET /api/me/tutor/sessions[/messages] · ver routers/tutor.py.
// Markdown rendering: plain text + line-breaks (sin react-markdown dep · MVP).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AuthIdentity, TutorMessage, TutorSession } from '../lib/types';
import {
  createTutorSession,
  fetchTutorMessages,
  isApiError,
  listTutorSessions,
  sendTutorMessage,
  submitTutorFeedback,
  TUTOR_QUESTION_MAX_CHARS,
} from '../lib/api';
import { TELEGRAM_SUPPORT_URL, WHATSAPP_SUPPORT_URL, ASESOR_CALL_URL } from '../lib/support-links';
import { OFFER_LINKS } from '../lib/offer-links';
import { useExtLink } from '../lib/external-links';

// ───────────────────────────────────────────────────────────────────────────
// Guardrail comercial (Jaime · 2026-06-17): el Tutor IA NO debe "vender" ni
// responder de precios. Las consultas comerciales/de precio se interceptan
// ANTES del RAG y se deriva a atención al cliente (WhatsApp). Los upgrades de
// plan ofrecen además checkout directo + agendar llamada con un asesor.
//
// Devuelve el cuerpo markdown de la respuesta a inyectar, o null si la consulta
// no es comercial (entonces sigue el flujo normal al RAG). Los enlaces salen de
// support-links.ts / offer-links.ts (hoy con fallback a soporte · ver TODO).
// ───────────────────────────────────────────────────────────────────────────
function commercialReply(
  text: string,
  ext: (key: string, fallback: string) => string,
): string | null {
  const t = text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''); // quita acentos para el match

  // Fase 2: enlaces gestionables desde el panel admin "Enlaces" (fallback hardcode).
  const whatsapp = ext('whatsapp_support', WHATSAPP_SUPPORT_URL);
  const asesor = ext('asesor_call', ASESOR_CALL_URL);
  const basicUpgrade = ext('basic_especial', OFFER_LINKS.basic.especial);
  const proUpgrade = ext('pro_especial', OFFER_LINKS.pro.especial);

  // Upgrade Start → Basic
  if (/start\s*(a|->|→)?\s*basic/.test(t) || /(pas|sub|cambi).*start.*basic/.test(t)) {
    return [
      'Pasar de **Start a Basic** es un cambio de plan 🚀 Tienes dos opciones:',
      '',
      `- 🛒 **Ir directo al producto** → [unirme a Basic](${basicUpgrade})`,
      `- 📞 **Hablar con un asesor** → [agendar llamada](${asesor})`,
      '',
      `Para dudas de precio o facturación te atiende [atención al cliente](${whatsapp}).`,
    ].join('\n');
  }

  // Upgrade Basic → Pro (precio especial: se descuenta el Basic ya pagado)
  if (/basic\s*(a|->|→)?\s*pro/.test(t) || /(pas|sub|cambi).*basic.*pro/.test(t)) {
    return [
      'Pasar de **Basic a Pro** tiene **precio especial**: se te descuenta el Basic que ya pagaste 🚀 Dos opciones:',
      '',
      `- 🛒 **Ir directo al upgrade** → [pasar a Pro](${proUpgrade})`,
      `- 📞 **Hablar con un asesor** → [agendar llamada](${asesor})`,
      '',
      `Para dudas de precio o facturación te atiende [atención al cliente](${whatsapp}).`,
    ].join('\n');
  }

  // Comercial / precio genérico → derivar a atención al cliente
  if (
    /(precio|cuanto cuesta|cuanto vale|cuanto sale|coste|descuento|oferta|promocion|pagar|pago|factura|reembolso|devoluc|tarjeta|comprar|contratar|suscrip|renovac|cancelar.*plan)/.test(
      t,
    )
  ) {
    return [
      'Para temas **comerciales o de precios** te atiende mejor una persona del equipo 🙌',
      '',
      `Escríbenos directamente a [atención al cliente](${whatsapp}) y te ayudamos enseguida.`,
    ].join('\n');
  }

  return null;
}

interface TutorSidebarProps {
  moduleSlug: string | null;
  /** Slug del curso · activa filtro contextual de sugerencias + welcome message. */
  courseSlug?: string | null;
  /** Título del módulo/lección actual · personaliza saludo + sugerencias contextuales. */
  moduleTitle?: string | null;
  auth: AuthIdentity | null;
}

interface DraftState {
  text: string;
  sending: boolean;
  error: string | null;
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; session: TutorSession | null; messages: TutorMessage[] }
  | { kind: 'error'; message: string };

// Estado del listado "Conversaciones anteriores" (lazy · solo se pide al abrirlo).
type HistoryState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; sessions: TutorSession[] }
  | { kind: 'error'; message: string };

// Fecha corta legible para el listado (ej. "5 jul 2026"). Best-effort.
function formatSessionDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

// Sugerencias por defecto · si moduleSlug/courseSlug match aplicamos contextuales.
// Default = Dashboard (sin curso ni módulo) → scope comercial + soporte general.
// Cubre los 5 vectores típicos del alumno desde Dashboard: planes, capital,
// activación licencia, pagos/facturación, eventos en vivo.
const DEFAULT_SUGGESTIONS: string[] = [
  '¿Qué incluye el plan Robotea Basic vs Pro?',
  '¿Cómo doy mis primeros pasos con Robotea?',
  '¿Cómo activo una licencia nueva?',
];

const COURSE_WELCOME: Record<string, { title: string; subtitle: string; suggestions: string[] }> = {
  'robotea-start': {
    title: 'Tutor IA · Robotea Start',
    subtitle: 'Tu primer robot de trading automatizado paso a paso. Pregúntame lo que necesites sobre setup VPS, MT5 y operativa real.',
    // Orden y contenido confirmados por Jaime (2026-06-17): primeros pasos/bróker,
    // activar primer robot, y la comercial "paso a Basic" (intercepta a CTA).
    suggestions: [
      'Primeros pasos: ¿qué bróker abro?',
      '¿Cómo activo el primer robot?',
      '¿Cuándo paso de Start a Basic?',
    ],
  },
  'robotea-basic': {
    title: 'Tutor IA · Robotea Basic',
    subtitle: 'Gestión de cartera con varios robots. Pregúntame sobre diversificación, riesgo y los robots adicionales.',
    // Orden confirmado por Jaime (2026-06-17): diversificación, % riesgo, robots
    // adicionales (sin "3"), y la comercial "paso a Pro" (intercepta a CTA).
    suggestions: [
      '¿Cómo funciona la diversificación entre robots?',
      '¿Qué % de riesgo aplico por estrategia?',
      '¿Qué robots adicionales tiene Basic?',
      '¿Cómo paso de Basic a Pro?',
    ],
  },
  'robotea-pro': {
    title: 'Tutor IA · Robotea Pro',
    subtitle: 'Escalada profesional con 6 robots y operativa multi-cuenta. Pregúntame sobre monetización, escalado y portafolio diversificado.',
    suggestions: [
      '¿Cómo se gestiona el riesgo y el drawdown?',
      '¿Qué es el bono Masterclass Portafolio?',
      '¿Cómo monetizo mis robots?',
      '¿Cuántas cuentas puedo operar a la vez?',
    ],
  },
};

// Sugerencias por módulo: dudas de la lección actual + un "siguiente paso" que
// guía la progresión del alumno (demo → real → ampliar portfolio).
const MODULE_SUGGESTIONS: Record<string, string[]> = {
  'mod1-vps-mt': [
    '¿Cómo contrato un VPS recomendado?',
    '¿Qué versión de MetaTrader debo instalar?',
    '¿Cómo conecto la cuenta demo?',
    'Ya tengo el VPS y MetaTrader listos, ¿qué hago ahora?',
  ],
  'mod2-golden-install': [
    '¿Dónde descargo el robot Golden Boot?',
    '¿Cómo lo añado al gráfico?',
    'No me sale el AutoTrading verde, ¿qué hago?',
    'Ya instalé el robot en demo, ¿cuál es el siguiente paso?',
  ],
  'mod3-config': [
    '¿Qué timeframe es el correcto?',
    '¿Cómo configuro el riesgo por operación?',
    '¿Qué pares debo activar?',
    'Ya lo tengo configurado, ¿qué viene ahora?',
  ],
  'mod4-capital-real': [
    '¿Qué broker recomiendan?',
    '¿Cómo verifico mi cuenta?',
    '¿Qué necesito para operar en real?',
    '¿Cómo doy el salto a cuenta real con seguridad?',
  ],
  'mod5-robot-real': [
    '¿Cómo paso del robot demo al real?',
    '¿Cómo ajusto el stop-loss?',
    '¿Qué hago si veo órdenes raras?',
    'Ya opero en real, ¿cómo superviso el sistema?',
  ],
  'mod6-monitoring': [
    '¿Cómo reviso resultados semanales?',
    '¿Cuándo debo ajustar el lote?',
    '¿Cómo interpreto la curva de equity?',
    '¿Cómo amplío mi portfolio de robots?',
  ],
};

// Sugerencias genéricas centradas en la lección actual + siguiente paso. Se usan
// cuando el alumno está dentro de un módulo que no tiene set específico → robusto
// a cambios del temario (no depende de slugs hardcodeados).
const LESSON_GENERIC_SUGGESTIONS: string[] = [
  'Explícame lo principal de esta lección',
  'Tengo una duda sobre lo que estoy viendo',
  '¿Cuál es el siguiente paso después de esta lección?',
];

function suggestionsFor(moduleSlug: string | null, courseSlug: string | null | undefined): string[] {
  // Prioridad: módulo específico > lección genérica (dentro de un módulo) > curso > default
  if (moduleSlug && MODULE_SUGGESTIONS[moduleSlug]) {
    return MODULE_SUGGESTIONS[moduleSlug]!;
  }
  if (moduleSlug) {
    return LESSON_GENERIC_SUGGESTIONS;
  }
  if (courseSlug && COURSE_WELCOME[courseSlug]) {
    return COURSE_WELCOME[courseSlug]!.suggestions;
  }
  return DEFAULT_SUGGESTIONS;
}

function welcomeFor(
  courseSlug: string | null | undefined,
  moduleTitle?: string | null,
): { title: string; subtitle: string } {
  // Si el alumno está dentro de una lección/módulo, personalizamos el saludo a ese contexto.
  if (moduleTitle) {
    return {
      title: `Tutor IA · ${moduleTitle}`,
      subtitle: 'Te acompaño en esta lección y en tu siguiente paso. Pregúntame lo que necesites 👋',
    };
  }
  if (courseSlug && COURSE_WELCOME[courseSlug]) {
    const c = COURSE_WELCOME[courseSlug]!;
    return { title: c.title, subtitle: c.subtitle };
  }
  // Dashboard · sin curso seleccionado · scope comercial + soporte amplio
  // (backend rutea a audience 'publica' del RAG · planes, precios, productos,
  //  facturación, soporte). Mensaje refleja el scope ampliado.
  return {
    title: 'Tu tutor de Robotea',
    subtitle:
      'Te acompaño paso a paso: instalar tu robot, pasar de demo a real y ampliar tu sistema de trading automatizado. Pregúntame lo que necesites 👋',
  };
}

// Render markdown compacto para la burbuja de chat (negritas, listas, tablas,
// enlaces, código). react-markdown auto-escapa HTML → seguro contra XSS.
const MD_COMPONENTS: Components = {
  p: ({ node, ...props }) => <p className="my-1 first:mt-0 last:mb-0 leading-snug" {...props} />,
  h1: ({ node, ...props }) => <h3 className="mt-2 mb-1 text-sm font-semibold text-ink" {...props} />,
  h2: ({ node, ...props }) => <h3 className="mt-2 mb-1 text-sm font-semibold text-ink" {...props} />,
  h3: ({ node, ...props }) => <h4 className="mt-2 mb-1 text-[13px] font-semibold text-ink" {...props} />,
  ul: ({ node, ...props }) => (
    <ul className="my-2 list-disc space-y-1 pl-5 marker:text-ink/50" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="my-2 list-decimal space-y-1 pl-5 marker:text-ink/50 marker:font-medium" {...props} />
  ),
  // [&>p]:my-0 → en listas "sueltas" (markdown con líneas en blanco) cada <li> envuelve
  // un <p>; sin esto el margen del párrafo separa el número/viñeta de su texto.
  li: ({ node, ...props }) => (
    <li className="pl-1 leading-snug [&>p]:my-0 [&>ul]:my-1 [&>ol]:my-1" {...props} />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold text-ink" {...props} />,
  a: ({ node, ...props }) => (
    // En CLARO el violeta-300 se lava sobre blanco → violeta sólido legible; oscuro conserva el claro.
    <a className="text-violet-700 underline dark:text-violet-300" target="_blank" rel="noopener noreferrer" {...props} />
  ),
  code: ({ node, ...props }) => (
    // bg-black/40 dejaba un chip casi negro sobre la burbuja clara; en CLARO usamos un wash de tinta.
    <code className="rounded bg-ink/10 px-1 py-0.5 text-[12px] dark:bg-black/40" {...props} />
  ),
  hr: () => <hr className="my-2 border-ink/10" />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="my-1 border-l-2 border-ink/20 pl-2 text-ink/70" {...props} />
  ),
  table: ({ node, ...props }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-[12px]" {...props} />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th className="border border-ink/15 px-2 py-1 text-left font-semibold" {...props} />
  ),
  td: ({ node, ...props }) => <td className="border border-ink/10 px-2 py-1 align-top" {...props} />,
};

function renderBody(body: string): JSX.Element {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
      {body}
    </ReactMarkdown>
  );
}

// ── Persistencia por pestaña del session_id activo ───────────────────────────
// sessionStorage: sobrevive a recargas (F5) en la misma pestaña, se limpia al
// cerrarla. try/catch → Safari modo privado / storage deshabilitado no rompe.
const TUTOR_SESSION_STORAGE_KEY = 'robotea:tutor:session-id';

function readStoredTutorSessionId(): string | null {
  try {
    return window.sessionStorage.getItem(TUTOR_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function storeTutorSessionId(sessionId: string): void {
  try {
    window.sessionStorage.setItem(TUTOR_SESSION_STORAGE_KEY, sessionId);
  } catch {
    /* storage no disponible · el chat funciona igual, solo no sobrevive a F5 */
  }
}

function clearStoredTutorSessionId(): void {
  try {
    window.sessionStorage.removeItem(TUTOR_SESSION_STORAGE_KEY);
  } catch {
    /* idem */
  }
}

export default function TutorSidebar({
  moduleSlug,
  courseSlug,
  moduleTitle,
  auth,
}: TutorSidebarProps): JSX.Element | null {
  const [open, setOpen] = useState<boolean>(false);
  const ext = useExtLink(); // Fase 2 · enlaces comerciales gestionables (fallback hardcode)

  // Escucha CustomEvent global · permite que cualquier botón del campus
  // abra el tutor sidebar (ej. botón 'Hablar con el Tutor IA' en Dashboard).
  useEffect(() => {
    function handleOpen(): void { setOpen(true); }
    window.addEventListener('robotea:open-tutor', handleOpen);
    return () => window.removeEventListener('robotea:open-tutor', handleOpen);
  }, []);
  const [load, setLoad] = useState<LoadState>({ kind: 'idle' });
  const [draft, setDraft] = useState<DraftState>({
    text: '',
    sending: false,
    error: null,
  });
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Respuesta del alumno a "¿Ha resuelto tu consulta?" en este turno · null = sin responder.
  const [localResolved, setLocalResolved] = useState<boolean | null>(null);
  // "Conversaciones anteriores": vista de listado (opt-in) + sus sesiones.
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [history, setHistory] = useState<HistoryState>({ kind: 'idle' });

  const suggestions = useMemo(
    () => suggestionsFor(moduleSlug, courseSlug),
    [moduleSlug, courseSlug],
  );
  const welcome = useMemo(() => welcomeFor(courseSlug, moduleTitle), [courseSlug, moduleTitle]);

  // Carga inicial: abrir en limpio en cada VISITA (Jaime 2026-06-17: nunca
  // reaparecer "la misma conversación antigua"), pero manteniendo la
  // conversación en curso si el alumno recarga la pestaña (Cristian
  // 2026-07-05: el tutor a veces pide refrescar el navegador y se perdía el
  // hilo). El session_id activo vive en sessionStorage → sobrevive a F5 en la
  // misma pestaña y desaparece al cerrarla, así la próxima visita arranca
  // limpia. La sesión se crea de forma perezosa en el primer envío; las
  // sesiones previas NO se borran (siguen en BD); "＋ Nueva consulta"
  // reinicia el chat y olvida el session_id guardado.
  const loadInitial = useCallback(async () => {
    if (!auth) return;
    const storedId = readStoredTutorSessionId();
    if (storedId) {
      try {
        const resumed = await fetchTutorMessages(storedId, auth);
        if (resumed.messages.length > 0) {
          setLoad({ kind: 'ready', session: resumed.session, messages: resumed.messages });
          return;
        }
      } catch {
        /* sesión inaccesible (expirada/borrada) → arranque limpio */
      }
      clearStoredTutorSessionId();
    }
    setLoad({ kind: 'ready', session: null, messages: [] });
  }, [auth]);

  useEffect(() => {
    if (open && load.kind === 'idle') {
      void loadInitial();
    }
  }, [open, load.kind, loadInitial]);

  // Auto-scroll al fondo al añadir mensaje
  useEffect(() => {
    if (load.kind !== 'ready') return;
    const node = scrollRef.current;
    if (!node) return;
    // Doble RAF para que el DOM se asiente antes de medir scrollHeight
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        node.scrollTop = node.scrollHeight;
      });
    });
  }, [load]);

  // Bloquea scroll body en modal mobile
  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Al cerrar el panel salimos de la vista de historial · la próxima apertura
  // arranca en el chat (coherente con "abre en limpio en cada visita").
  useEffect(() => {
    if (!open) setHistoryOpen(false);
  }, [open]);

  const handleSend = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text || !auth) return;
      if (text.length > TUTOR_QUESTION_MAX_CHARS) return;

      setLocalResolved(null); // nueva pregunta → re-permitir el feedback al responder

      // Asegurar sesión (lazy create)
      let activeSession: TutorSession | null =
        load.kind === 'ready' ? load.session : null;
      const baseMessages: TutorMessage[] =
        load.kind === 'ready' ? load.messages : [];

      // Guardrail comercial: si es consulta de precio/upgrade, NO va al RAG.
      // Respondemos localmente derivando a atención al cliente / checkout
      // (sin persistir en BD: es un nudge, no una conversación del tutor).
      const commercial = commercialReply(text, ext);
      if (commercial) {
        const now = Date.now();
        const userMsg: TutorMessage = {
          id: `commercial-user-${now}`,
          role: 'user',
          body: text,
          confidence: null,
          escalated: false,
          created_at: new Date().toISOString(),
        };
        const botMsg: TutorMessage = {
          id: `commercial-bot-${now}`,
          role: 'assistant',
          body: commercial,
          confidence: null,
          escalated: false,
          created_at: new Date().toISOString(),
        };
        setLoad({
          kind: 'ready',
          session: activeSession,
          messages: [...baseMessages, userMsg, botMsg],
        });
        setDraft({ text: '', sending: false, error: null });
        return;
      }

      setDraft({ text: '', sending: true, error: null });

      try {
        if (!activeSession) {
          activeSession = await createTutorSession(moduleSlug, auth);
          storeTutorSessionId(activeSession.session_id);
        }
        // Optimistic add user message
        const optimistic: TutorMessage = {
          id: `optimistic-${Date.now()}`,
          role: 'user',
          body: text,
          confidence: null,
          escalated: false,
          created_at: new Date().toISOString(),
        };
        setLoad({
          kind: 'ready',
          session: activeSession,
          messages: [...baseMessages, optimistic],
        });

        const resp = await sendTutorMessage(activeSession.session_id, text, auth);

        // Recompose: replace optimistic with confirmed via refetch (autoritativo
        // server-side · evita drift en confidence/escalated/server timestamps).
        const refreshed = await fetchTutorMessages(activeSession.session_id, auth);
        setLoad({
          kind: 'ready',
          session: refreshed.session,
          messages: refreshed.messages,
        });
        // Side-effect mantener última respuesta visible
        void resp;
      } catch (err: unknown) {
        const message = isApiError(err)
          ? err.detail
          : err instanceof Error
            ? err.message
            : 'Error desconocido';
        setDraft({ text, sending: false, error: message });
        // Roll back optimistic
        setLoad({
          kind: 'ready',
          session: activeSession,
          messages: baseMessages,
        });
        return;
      }
      setDraft({ text: '', sending: false, error: null });
    },
    [auth, moduleSlug, load, ext],
  );

  // "Nueva consulta": deja el chat limpio (saludo + sugerencias contextuales de la
  // lección/módulo actual) SIN borrar el historial. La sesión anterior queda en BD;
  // la próxima pregunta crea una sesión nueva (lazy create en handleSend).
  const handleNewQuery = useCallback(() => {
    clearStoredTutorSessionId();
    setLoad({ kind: 'ready', session: null, messages: [] });
    setDraft({ text: '', sending: false, error: null });
    setLocalResolved(null);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }, []);

  // "Conversaciones anteriores": abre la vista de listado y (re)carga las
  // sesiones del alumno. Lazy · nunca se pide al abrir el panel (respeta #697:
  // el chat arranca en limpio, el historial es siempre una acción explícita).
  const openHistory = useCallback(async () => {
    setHistoryOpen(true);
    if (!auth) return;
    setHistory({ kind: 'loading' });
    try {
      const { sessions } = await listTutorSessions(auth);
      // Solo conversaciones reales (con al menos un mensaje). Las sesiones se
      // crean de forma perezosa al primer envío, pero filtramos por robustez.
      const withMsgs = sessions.filter((s) => (s.message_count ?? 0) > 0);
      setHistory({ kind: 'ready', sessions: withMsgs });
    } catch (err: unknown) {
      const message = isApiError(err)
        ? err.detail
        : err instanceof Error
          ? err.message
          : 'No se pudieron cargar las conversaciones';
      setHistory({ kind: 'error', message });
    }
  }, [auth]);

  // Seleccionar una conversación anterior: carga sus mensajes y la convierte en
  // la sesión ACTIVA (persistimos su id en sessionStorage con la key existente,
  // así sobrevive a un F5 y "＋ Nueva consulta" la olvida como cualquier otra).
  const handleSelectSession = useCallback(
    async (sessionId: string) => {
      if (!auth) return;
      setHistoryOpen(false);
      setLoad({ kind: 'loading' });
      setDraft({ text: '', sending: false, error: null });
      setLocalResolved(null);
      try {
        const resumed = await fetchTutorMessages(sessionId, auth);
        storeTutorSessionId(sessionId);
        setLoad({
          kind: 'ready',
          session: resumed.session,
          messages: resumed.messages,
        });
      } catch (err: unknown) {
        const message = isApiError(err)
          ? err.detail
          : err instanceof Error
            ? err.message
            : 'No se pudo cargar la conversación';
        setLoad({ kind: 'error', message });
      }
    },
    [auth],
  );

  // Feedback "¿Ha resuelto tu consulta?" · best-effort (no rompe el chat si falla).
  const handleFeedback = useCallback(
    async (resolved: boolean) => {
      setLocalResolved(resolved);
      const session = load.kind === 'ready' ? load.session : null;
      if (session && auth) {
        try {
          await submitTutorFeedback(session.session_id, resolved, auth);
        } catch {
          /* señal best-effort · la UX no depende de que se guarde */
        }
      }
      if (!resolved) {
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    },
    [load, auth],
  );

  const onTextareaKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSend(draft.text);
      }
    },
    [draft.text, handleSend],
  );

  if (!auth) return null;

  const isEmpty =
    load.kind === 'ready' && load.messages.length === 0 && !draft.sending;
  const charCount = draft.text.length;
  const charOver = charCount > TUTOR_QUESTION_MAX_CHARS;
  const sendDisabled =
    draft.sending || draft.text.trim().length === 0 || charOver;

  return (
    <>
      {/* Floating toggle button · sticky right · visible siempre cuando panel cerrado */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Abrir tutor IA"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 rounded-full bg-violet-500 px-4 py-3 text-sm font-medium text-white shadow-2xl shadow-violet-500/40 transition hover:scale-105 hover:bg-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300/60 lg:bottom-8 lg:right-8"
        >
          <span aria-hidden="true">💬</span>
          <span>Tutor IA</span>
        </button>
      )}

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop · solo mobile (lg: panel lateral sin oscurecer) */}
            <motion.div
              key="tutor-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
              aria-hidden="true"
            />

            <motion.aside
              key="tutor-panel"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 260, damping: 32 }}
              role="dialog"
              aria-label="Tutor IA Robotea"
              className="fixed inset-0 z-50 flex flex-col bg-canvas text-ink shadow-2xl lg:inset-y-4 lg:right-4 lg:left-auto lg:w-[400px] lg:rounded-2xl lg:border lg:border-ink/10"
            >
              {/* Header */}
              <header className="border-b border-ink/10 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-ink/40">
                      Tutor IA
                    </p>
                    <h2 className="truncate text-sm font-semibold text-ink">
                      {welcome.title}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Cerrar tutor"
                    className="shrink-0 rounded-md p-2 text-ink/60 transition hover:bg-ink/5 hover:text-ink"
                  >
                    <span aria-hidden="true">✕</span>
                  </button>
                </div>
                <p className="mt-1.5 text-xs leading-snug text-ink/75">
                  {welcome.subtitle}
                </p>
                {!historyOpen && (
                  <button
                    type="button"
                    onClick={() => void openHistory()}
                    className="mt-2 inline-flex items-center gap-1 rounded-full border border-ink/10 bg-ink/5 px-2.5 py-1 text-[11px] font-medium text-ink/60 transition hover:bg-ink/10 hover:text-ink/80"
                  >
                    <span aria-hidden="true">🕘</span>
                    Conversaciones anteriores
                  </button>
                )}
              </header>

              {historyOpen ? (
                /* Vista "Conversaciones anteriores" · listado seleccionable */
                <div className="flex flex-1 flex-col overflow-hidden">
                  <div className="flex items-center gap-2 border-b border-ink/10 px-4 py-2">
                    <button
                      type="button"
                      onClick={() => setHistoryOpen(false)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-ink/60 transition hover:bg-ink/5 hover:text-ink"
                    >
                      <span aria-hidden="true">←</span>
                      Volver al chat
                    </button>
                  </div>
                  <div
                    className="flex-1 overflow-y-auto px-3 py-3"
                    role="list"
                    aria-label="Conversaciones anteriores"
                  >
                    {history.kind === 'loading' && (
                      <p className="text-xs text-ink/40">Cargando conversaciones…</p>
                    )}

                    {history.kind === 'error' && (
                      <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-100">
                        <p>No hemos podido cargar tus conversaciones.</p>
                        <p className="mt-1 text-red-700/70 dark:text-red-100/70">
                          {history.message}
                        </p>
                        <button
                          type="button"
                          onClick={() => void openHistory()}
                          className="mt-2 rounded-md border border-ink/20 px-2 py-1 text-xs hover:bg-ink/5"
                        >
                          Reintentar
                        </button>
                      </div>
                    )}

                    {history.kind === 'ready' && history.sessions.length === 0 && (
                      <p className="px-1 py-4 text-center text-xs text-ink/50">
                        Aún no tienes conversaciones guardadas. Cuando hables con el
                        tutor, aparecerán aquí para retomarlas cuando quieras.
                      </p>
                    )}

                    {history.kind === 'ready' && history.sessions.length > 0 && (
                      <ul className="space-y-2">
                        {history.sessions.map((s) => (
                          <li key={s.session_id}>
                            <button
                              type="button"
                              onClick={() => void handleSelectSession(s.session_id)}
                              className="w-full rounded-lg border border-ink/10 bg-ink/5 px-3 py-2 text-left transition hover:bg-ink/10"
                            >
                              <p className="line-clamp-2 text-xs font-medium text-ink/85">
                                {s.title || 'Consulta sin título'}
                              </p>
                              <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-ink/40">
                                <span>
                                  {formatSessionDate(s.last_message_at || s.created_at)}
                                </span>
                                {s.module_slug && (
                                  <span className="rounded bg-ink/10 px-1.5 py-0.5 text-ink/50">
                                    {s.module_slug}
                                  </span>
                                )}
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              ) : (
              <>
              {/* Thread */}
              <div
                ref={scrollRef}
                className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
                role="log"
                aria-live="polite"
              >
                {load.kind === 'loading' && (
                  <p className="text-xs text-ink/40">Cargando conversación…</p>
                )}

                {load.kind === 'error' && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-100">
                    <p>No hemos podido cargar el tutor.</p>
                    <p className="mt-1 text-red-700/70 dark:text-red-100/70">{load.message}</p>
                    <button
                      type="button"
                      onClick={() => {
                        setLoad({ kind: 'idle' });
                      }}
                      className="mt-2 rounded-md border border-ink/20 px-2 py-1 text-xs hover:bg-ink/5"
                    >
                      Reintentar
                    </button>
                  </div>
                )}

                {isEmpty && (
                  <div className="space-y-3">
                    <p className="text-sm text-ink/70">
                      {moduleSlug || courseSlug
                        ? 'Hola 👋 Soy tu tutor de Robotea y te acompaño paso a paso con el curso, la instalación del robot y el VPS. Empieza por una de estas preguntas o escríbeme la tuya.'
                        : 'Hola 👋 Soy tu tutor de Robotea y te acompaño en tu camino: instalar tu robot, pasar a real y ampliar tu sistema de trading automatizado. Empieza por una de estas preguntas o escríbeme la tuya.'}
                    </p>
                    <ul className="space-y-2">
                      {suggestions.map((q) => (
                        <li key={q}>
                          <button
                            type="button"
                            onClick={() => void handleSend(q)}
                            className="w-full rounded-lg border border-ink/10 bg-ink/5 px-3 py-2 text-left text-xs text-ink/80 transition hover:bg-ink/10"
                          >
                            {q}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {load.kind === 'ready' &&
                  load.messages.map((msg) => {
                    const isUser = msg.role === 'user';
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={
                            isUser
                              // Burbuja usuario = relleno violeta saturado en ambos temas → texto blanco
                              // (text-ink en CLARO era navy sobre violeta = bajo contraste).
                              ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-violet-500/90 px-3 py-2 text-sm text-white shadow-sm'
                              : 'max-w-[88%] rounded-2xl rounded-bl-sm border border-ink/10 bg-ink/5 px-3 py-2 text-sm text-ink/90 shadow-sm'
                          }
                        >
                          <div className="break-words">
                            {renderBody(msg.body)}
                          </div>
                        </div>
                      </div>
                    );
                  })}

                {draft.sending && load.kind === 'ready' && (
                  <div className="flex justify-start">
                    <div className="max-w-[60%] rounded-2xl rounded-bl-sm border border-ink/10 bg-ink/5 px-3 py-2 text-sm">
                      <span className="inline-flex items-center gap-1 text-ink/40">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink/50" />
                        <span
                          className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink/50"
                          style={{ animationDelay: '120ms' }}
                        />
                        <span
                          className="h-1.5 w-1.5 animate-bounce rounded-full bg-ink/50"
                          style={{ animationDelay: '240ms' }}
                        />
                        <span className="ml-1">Robotea está pensando…</span>
                      </span>
                    </div>
                  </div>
                )}

                {/* Feedback de resolución · "¿Ha resuelto tu consulta?" (señal Uso B) */}
                {load.kind === 'ready'
                  && !draft.sending
                  && load.messages.some((m) => m.role === 'assistant')
                  && localResolved === null
                  && (load.session?.resolved ?? null) === null && (
                    <div className="mt-3 rounded-xl border border-ink/10 bg-ink/5 px-3 py-3 text-center">
                      <p className="text-xs font-medium text-ink/75">
                        ¿Ha resuelto tu consulta?
                      </p>
                      <div className="mt-2 flex justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => void handleFeedback(true)}
                          className="rounded-full border border-emerald-400/40 bg-emerald-500/10 px-6 py-1 text-xs font-medium text-emerald-700 dark:text-emerald-200 transition hover:bg-emerald-500/20"
                        >
                          Sí
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleFeedback(false)}
                          className="rounded-full border border-ink/15 bg-ink/5 px-6 py-1 text-xs font-medium text-ink/70 transition hover:bg-ink/10"
                        >
                          No
                        </button>
                      </div>
                    </div>
                  )}

                {localResolved === true && (
                  <div className="mt-3 rounded-xl border border-emerald-400/20 bg-emerald-500/5 px-3 py-2.5 text-center text-xs text-emerald-700 dark:text-emerald-100">
                    ¡Genial! 🎉 Me alegro de haberte ayudado. Pulsa{' '}
                    <strong>＋ Nueva consulta</strong> abajo para empezar otra.
                  </div>
                )}

                {localResolved === false && (
                  <div className="mt-3 rounded-xl border border-violet-400/20 bg-violet-500/5 px-3 py-2.5 text-center text-xs text-violet-700 dark:text-violet-100">
                    Sin problema 🙌 ¿En qué te puedo seguir ayudando? Escríbeme tu
                    duda abajo.
                  </div>
                )}
              </div>

              {/* Input */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void handleSend(draft.text);
                }}
                className="border-t border-ink/10 px-3 py-3"
              >
                {draft.error && (
                  <p className="mb-2 text-[11px] text-rose-600 dark:text-rose-300">
                    {draft.error}
                  </p>
                )}
                <textarea
                  ref={textareaRef}
                  value={draft.text}
                  onChange={(e) =>
                    setDraft((prev) => ({ ...prev, text: e.target.value }))
                  }
                  onKeyDown={onTextareaKeyDown}
                  rows={2}
                  maxLength={TUTOR_QUESTION_MAX_CHARS + 50 /* allow overflow visible para char counter feedback */}
                  placeholder={
                    moduleSlug || courseSlug
                      ? 'Escribe tu pregunta sobre Robotea Start…'
                      : 'Escribe tu pregunta sobre Robotea…'
                  }
                  className={
                    // Input de envío: en CLARO wash de tinta (bg-ink/5); el bg-black/40 (recuadro
                    // oscuro flagrante en CLARO) queda solo en OSCURO via dark:bg-black/40.
                    'w-full resize-none rounded-lg border border-ink/10 bg-ink/5 px-3 py-2 text-sm text-ink placeholder:text-ink/30 focus:border-violet-400/60 focus:outline-none focus:ring-1 focus:ring-violet-400/40 dark:bg-black/40'
                  }
                  aria-label="Pregunta para el tutor IA"
                  disabled={draft.sending}
                />
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <span
                    className={
                      charOver
                        ? 'shrink-0 text-[11px] text-rose-600 dark:text-rose-300'
                        : 'shrink-0 text-[11px] text-ink/40'
                    }
                  >
                    {charCount}/{TUTOR_QUESTION_MAX_CHARS}
                  </span>
                  {load.kind === 'ready' && load.messages.length > 0 && (
                    <button
                      type="button"
                      onClick={handleNewQuery}
                      title="Empieza un chat limpio · tu historial se guarda"
                      className="inline-flex items-center gap-1 rounded-full border border-violet-400/40 bg-violet-500/10 px-3 py-1 text-[11px] font-medium text-violet-700 transition hover:border-violet-300/60 hover:bg-violet-500/20 dark:text-violet-100"
                    >
                      <span aria-hidden="true">＋</span>
                      Nueva consulta
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={sendDisabled}
                    className="rounded-lg bg-violet-500 px-4 py-1.5 text-xs font-medium text-white transition hover:bg-violet-400 disabled:cursor-not-allowed disabled:bg-ink/10 disabled:text-ink/40"
                  >
                    {draft.sending ? 'Enviando…' : 'Enviar'}
                  </button>
                </div>
                <p className="mt-2 text-[10px] text-ink/30">
                  Las respuestas las genera una IA con el conocimiento de
                  Robotea. Verifica siempre antes de operar con dinero real.
                </p>
                {/* El tutor IA ayuda con los PASOS dentro del campus. Para
                    soporte técnico humano el canal oficial es Telegram. */}
                <p className="mt-2 border-t border-ink/10 pt-2 text-[11px] text-ink/45">
                  ¿Soporte técnico?{' '}
                  <a
                    href={TELEGRAM_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-violet-700 underline hover:text-violet-600 dark:text-violet-300 dark:hover:text-violet-200"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M21.94 4.6 18.6 20.36c-.25 1.1-.9 1.38-1.83.86l-5.05-3.72-2.44 2.35c-.27.27-.5.5-1.02.5l.36-5.16 9.4-8.5c.4-.36-.09-.56-.63-.2L5.16 13.3l-5-1.56c-1.08-.34-1.1-1.08.23-1.6L20.54 3.04c.9-.34 1.69.2 1.4 1.56Z" />
                    </svg>
                    Telegram
                  </a>
                </p>
              </form>
              </>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
